import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createAsset } from '../../api/assetAPI';
import { createAssetMeter } from '../../api/asset-meterAPI';
import { createGroup } from '../../api/groupAPI';
import { useUserProfile } from '../../masterLayout/MasterLayoutBody';

const AssetImporter = ({ onComplete }) => {
  const userProfile = useUserProfile();
  const handleDataImport = async (rows, onProgress) => {
    const createdGroupsCache = new Map();
    let count = 0;
    for (const row of rows) {
      try {
        let groupId = row.groupId;

        if (row._pendingGroupCreation) {
          const { groupName, manufacturerId, classId } = row._pendingGroupCreation;
          const manufacturerIdValue = Number(manufacturerId);
          const classIdValue = Number(classId);
          const cacheKey = `${manufacturerIdValue || ''}|${classIdValue || ''}|${String(groupName).toLowerCase()}`;

          if (createdGroupsCache.has(cacheKey)) {
            groupId = createdGroupsCache.get(cacheKey);
          } else {
            try {
              const createdGroup = await createGroup({
                groupName,
                manufacturerId: manufacturerIdValue,
                classId: classIdValue
              });
              if (createdGroup?.id) {
                groupId = createdGroup.id;
                createdGroupsCache.set(cacheKey, createdGroup.id);
              } else {
                console.error('Group creation returned invalid payload', createdGroup);
              }
            } catch (err) {
              console.error('Failed to create group during asset import', err);
            }
          }

          if (!groupId) {
            throw new Error(`Unable to create group "${groupName}" during import`);
          }

          row.groupId = groupId;
          delete row._pendingGroupCreation;
        }
        const createdAsset = await createAsset({
          assetNumber: row.assetNumber,
          assetDescription: row.assetDescription,
          assetDepartmentId: row.assetDepartmentId,
          costCenterId: row.costCenterId,
          assetSerial: row.assetSerial,
          assetStartDate: row.assetStartDate,
          assetEndDate: row.assetEndDate,
          AssetTypeId: row.AssetTypeId,
          PlanningTypeId: row.PlanningTypeId,
          groupId,
          plantStatusId: row?.plantStatusId || 1,
          assetCriticalityId: row?.assetCriticalityId || 2,
          organisationId: 1,
          assetAltNumber: row.assetAltNumber,
          buildYear: row.buildYear,
          assetFloc: row.assetFloc,
          createdBy: userProfile?.id,
        });

        if (createdAsset?.id && row.meterId) {
          try {
            await createAssetMeter({
              assetId: createdAsset.id,
              meterId: row.meterId,
              primaryMeter: 1,
              startReading:
                row.startReading !== undefined && row.startReading !== ''
                  ? Number(row.startReading)
                  : 0,
              utilisation:
                row.utilisation !== undefined && row.utilisation !== ''
                  ? Number(row.utilisation)
                  : 0,
              endOfLife:
                row.endOfLife !== undefined && row.endOfLife !== ''
                  ? Number(row.endOfLife)
                  : 0,
            });
          } catch (err) {
            console.error('Failed to create asset meter during asset import', err);
          }
        }
      } catch (err) {
        console.error('Failed to import asset', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>assets</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className="mb-4">
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>assetNumber</td>
                      <td>Text</td>
                      <td>Unique identifier/code for the asset</td>
                    </tr>
                    <tr>
                      <td>assetDescription</td>
                      <td>Text</td>
                      <td>Description of the asset</td>
                    </tr>
                    <tr>
                      <td>assetSerial</td>
                      <td>Text</td>
                      <td>Serial number or Vehicle Identification (VIN) of the asset. Required for Advanced type assets.</td>
                    </tr>
                    <tr>
                      <td>assetDepartmentId</td>
                      <td>Lookup</td>
                      <td>Valid department name from the system</td>
                    </tr>
                    <tr>
                      <td>costCenterId</td>
                      <td>Lookup</td>
                      <td>Valid cost center description from the system</td>
                    </tr>
                    <tr>
                      <td>assetStartDate</td>
                      <td>Date</td>
                      <td>Operational start date in DD/MM/YYYY format</td>
                    </tr>
                    <tr>
                      <td>assetEndDate</td>
                      <td>Date</td>
                      <td>Operational end date in DD/MM/YYYY format</td>
                    </tr>
                    <tr>
                      <td>PlanningTypeId</td>
                      <td>Lookup</td>
                      <td>Planning type name from the system. Advanced | Standard | Basic</td>
                    </tr>
                    <tr>
                      <td>AssetTypeId</td>
                      <td>Lookup</td>
                      <td>Asset type name from the system</td>
                    </tr>
                    <tr>
                      <td>groupId</td>
                      <td>Lookup</td>
                      <td>Valid Group name the asset belongs to</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>plantStatusId</td>
                      <td>Lookup</td>
                      <td>Valid plant status for the asset. Will default to 'Operational' if left blank.</td>
                    </tr>
                    <tr>
                      <td>assetCriticalityId</td>
                      <td>Lookup</td>
                      <td>Criticality description for the asset. High | Medium | Low</td>
                    </tr>
                    <tr>
                      <td>buildYear</td>
                      <td>Text</td>
                      <td>Build date of the asset in YYYY or MM/YYYYformat</td>
                    </tr>
                    <tr>
                      <td>assetCode</td>
                      <td>Text</td>
                      <td>Additional identification code for the asset</td>
                    </tr>
                    <tr>
                      <td>manufacturerId</td>
                      <td>Text</td>
                      <td>Manufacturer (ie Caterpillar) This will auto-load missing groups if populated</td>
                    </tr>
                    <tr>
                      <td>classId</td>
                      <td>Text</td>
                      <td>Asset class (ie Dump Truck) This will auto-load missing groups if populated</td>
                    </tr>
                    <tr>
                      <td>floc</td>
                      <td>Text</td>
                      <td>Functional location of the asset</td>
                    </tr>
                    <tr>
                      <td>assetAltNumber</td>
                      <td>Text</td>
                      <td>Alternate unit number for the asset</td>
                    </tr>
                    <tr>
                      <td>meterId</td>
                      <td>Lookup</td>
                      <td>Meter to associate with the imported asset</td>
                    </tr>
                    <tr>
                      <td>startReading</td>
                      <td>Number</td>
                      <td>Initial reading for the linked meter. Defaults to 0 if blank.</td>
                    </tr>
                    <tr>
                      <td>utilisation</td>
                      <td>Number</td>
                      <td>Expected utilisation value for the meter. Defaults to 0 if blank.</td>
                    </tr>
                    <tr>
                      <td>endOfLife</td>
                      <td>Number</td>
                      <td>End of life value for the meter. Defaults to 0 if blank.</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity="assets"
          requiredFields={[
            {
              key: 'assetNumber',
              type: 'string',
              required: true,
              unique: true
            },
            {
              key: 'assetDescription',
              type: 'string',
              required: true
            },
            {
              key: 'assetDepartmentId',
              type: 'lookup',
              required: true,
              api: 'hierarchy-structures',
              nameField: 'name',
            },
            {
              key: 'costCenterId',
              type: 'lookup',
              required: true,
              api: 'cost-centres',
              nameField: 'costCenterDescription',
            },
            {
              key: 'assetStartDate',
              type: 'date',
              required: true,
            },
            {
              key: 'assetEndDate',
              type: 'date',
              required: true,
            },
            {
              key: 'PlanningTypeId',
              type: 'lookup',
              required: true,
              api: 'planning-types',
              nameField: 'typeDescription',
            },
            {
              key: 'AssetTypeId',
              type: 'lookup',
              required: true,
              api: 'asset-type',
              nameField: 'name',
            },
            {
              key: 'manufacturerId',
              type: 'lookup',
              api: 'manufacturers',
              nameField: 'manufacturer',
            },
            {
              key: 'classId',
              type: 'lookup',
              api: 'class',
              nameField: 'className',
            },
            {
              key: 'groupId',
              type: 'lookup',
              required: true,
              api: 'groups',
              nameField: 'groupName',
            },
            {
              key: 'plantStatusId',
              type: 'lookup',
              required: true,
              api: 'plant-status',
              nameField: 'status',
            },
            {
              key: 'assetSerial',
              type: 'string',
              unique: true,
            },
            {
              key: 'assetCriticalityId',
              type: 'lookup',
              api: 'asset-criticality',
              nameField: 'criticalityName',
            },
            {
              key: 'buildYear',
              type: 'string',
            },
            {
              key: 'assetCode',
              type: 'string',
            },
            {
              key: 'assetFloc',
              type: 'string',
            },
            {
              key: 'assetAltNumber',
              type: 'string',
            },
            {
              key: 'meterId',
              type: 'lookup',
              required: false,
              api: 'meters',
              nameField: 'name',
            },
            {
              key: 'startReading',
              type: 'number',
              required: false,
            },
            {
              key: 'utilisation',
              type: 'number',
              required: false,
            },
            {
              key: 'endOfLife',
              type: 'number',
              required: false,
            },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />

      </div>
    </>
  );
};

export default AssetImporter;
